import { useWorkspaceObjectives } from '@/lib/workspace-objectives';
import { useAppWorkList } from '@/lib/app-work';
import { AppInbox } from '@/components/apps/AppInbox';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TodayObjectives } from '@/components/objectives/TodayObjectives';
import { WorkObjectivesTab } from '@/components/objectives/WorkObjectivesTab';
import { TodayRituals } from '@/components/rituals/TodayRituals';
import { useDueRituals } from '@/components/rituals/useDueRituals';
import { useWorkHandoffs } from '@/lib/work-handoffs';
import { scopeOf, useIsTenantManager } from '@/lib/team-scope';
import { ScopeSwitch } from '@/components/activity/ScopeSwitch';
import { TeamWeek } from '@/components/activity/TeamWeek';
import { PlaysReview } from '@/components/activity/PlaysReview';
import { Search } from 'lucide-react';
import type {
  ActivitySubjectActionSummary,
  ActivitySubjectCard as ActivitySubjectCardType,
  ActivitySubjectDetail,
  ActivitySubjectRecommendation,
  ActivitySurfaceMode,
} from '@shared/types/activity-feed';
import type {
  DashboardChatPageAction,
  DashboardChatPageItem,
} from '@shared/types/dashboard-chat';
import { ActivityActionQueue } from '@/components/activity/ActivityActionQueue';
import { TodayWorkList } from '@/components/activity/TodayWorkList';
import { useLivingWorkGestures } from '@/components/activity/useLivingWorkGestures';
import { useThreadStateRealtime } from '@/components/activity/useThreadStateRealtime';
import { useProposalExpiryRefresh } from '@/components/activity/useProposalExpiryRefresh';
import {
  isUnattachedActivitySubject,
  projectActivityLivingAccounts,
} from '@/components/activity/thread-living-model';
import { ACCOUNT_TASK_COMPLETED_EVENT } from '@/components/activity/AccountCapabilityLauncher';
import { ActivityEmptyState } from '@/components/activity/ActivityEmptyState';
import { ActivityJournalView } from '@/components/activity/ActivityJournalView';
import { ActivitySubjectDrawer } from '@/components/activity/ActivitySubjectDrawer';
import { ActivitySubjectTable } from '@/components/activity/ActivitySubjectTable';
import {
  type ActivitySortMode,
} from '@/components/activity/ActivitySubjectTableControls';
import {
  CATEGORY_LABELS,
  deriveActivityRegisterState,
  deriveActivitySubjectType,
  getActivityPriorityRank,
  RECOMMENDED_ACTION_KIND_LABELS,
  STATE_LABELS,
} from '@/components/activity/activity-ui';
import {
  archiveActivitySubject,
  dismissActivityRecommendation,
  getActivitySubjectDetail,
  listActivitySubjects,
  syncKnownGmailThreads,
} from '@/lib/activity-feed';
import { useRegisterAppAssistantContext } from '@/lib/app-assistant-context';
import { buildSubjectThreadPath } from '@/lib/thread-routes';
import { prepareAccountTaskPlan } from '@/lib/task-action';


const matchesSearch = (subject: ActivitySubjectCardType, query: string): boolean => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;

  const haystack = [
    subject.subjectLabel,
    subject.subjectSubtitle,
    subject.headline,
    subject.nextStepLabel,
    ...subject.chips.map((chip) => chip.label),
    ...(subject.recommendedActions ?? []).flatMap((action) => [
      action.label,
      RECOMMENDED_ACTION_KIND_LABELS[action.kind],
    ]),
    CATEGORY_LABELS[subject.category],
    STATE_LABELS[deriveActivityRegisterState(subject)],
    deriveActivitySubjectType(subject),
  ]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalizedQuery);
};

const sortActivitySubjects = (
  left: ActivitySubjectCardType,
  right: ActivitySubjectCardType,
  sortMode: ActivitySortMode
): number => {
  if (sortMode === 'az') {
    return left.subjectLabel.localeCompare(right.subjectLabel, 'fr');
  }

  if (sortMode === 'priority') {
    const priorityDelta = getActivityPriorityRank(left) - getActivityPriorityRank(right);
    if (priorityDelta !== 0) return priorityDelta;
  }

  const leftTime = left.latestEventAt ? new Date(left.latestEventAt).getTime() : 0;
  const rightTime = right.latestEventAt ? new Date(right.latestEventAt).getTime() : 0;
  if (leftTime !== rightTime) return rightTime - leftTime;

  return left.subjectLabel.localeCompare(right.subjectLabel, 'fr');
};

const buildOpenThreadPageAction = (
  subject: {
    subjectId?: string | null;
    subjectKey: string | null;
    subjectKind: string;
    subjectLabel: string;
    headline?: string | null;
    nextStepLabel?: string | null;
    nextStepDescription?: string | null;
  },
  id: string
): DashboardChatPageAction => ({
  id,
  kind: 'navigation',
  label: `Ouvrir le compte ${subject.subjectLabel}`,
  description:
    subject.nextStepDescription ?? subject.nextStepLabel ?? subject.headline ?? null,
  href: buildSubjectThreadPath({
    subjectId: subject.subjectId,
    subjectKey: subject.subjectKey,
    subjectKind: subject.subjectKind,
    subjectLabel: subject.subjectLabel,
  }),
  actionLabel: 'Ouvrir le compte',
});

const buildThreadPageItem = (
  subject: {
    subjectKind: string;
    subjectLabel: string;
    subjectSubtitle?: string | null;
    headline?: string | null;
    nextStepLabel?: string | null;
    nextStepDescription?: string | null;
    severity?: string | null;
  },
  id: string,
  primaryActionId: string
): DashboardChatPageItem => ({
  id,
  label: subject.subjectLabel,
  summary:
    subject.nextStepDescription ??
    subject.nextStepLabel ??
    subject.headline ??
    subject.subjectSubtitle ??
    null,
  meta: subject.subjectSubtitle ?? subject.subjectKind,
  priority: subject.severity ?? null,
  primaryActionId,
});

const getActivitySubjectStableId = (subject: {
  subjectId?: string | null;
  subjectKey: string | null;
  subjectKind: string;
  subjectLabel: string;
}): string =>
  subject.subjectId ??
  subject.subjectKey ??
  `${subject.subjectKind}:${subject.subjectLabel}`;

const toActionSummary = (
  recommendation: ActivitySubjectRecommendation
): ActivitySubjectActionSummary => ({
  id: recommendation.id,
  label: recommendation.label,
  summary: recommendation.summary,
  kind: 'other',
  priority: recommendation.priority,
  state: recommendation.state,
  action: recommendation.action,
  dismissible: recommendation.dismissible,
  sourceRunId: recommendation.sourceRunId,
  sourceActionId: recommendation.sourceActionId,
  sourceActionIndex: recommendation.sourceActionIndex,
  continuationPlanningStatus: recommendation.continuationPlanningStatus,
});

export const ActivityPage = () => {
  const [pageParams, setPageParams] = useSearchParams();
  const { objectives, userId: objectiveUserId } = useWorkspaceObjectives();
  const activeObjectivesSummary = objectives.filter((goal) => goal.status === 'active' && (goal.scope === 'company' || goal.owner_user_id === objectiveUserId)).slice(0, 4).map((goal) => `${goal.scope === 'company' ? 'Entreprise' : 'Individuel'} : ${goal.title}${goal.due_date ? `, échéance ${goal.due_date}` : ''}`).join('; ');
  const [mode, setMode] = useState<ActivitySurfaceMode>(pageParams.get('history') === '1' ? 'history' : 'subjects');
  const [sortMode, setSortMode] = useState<ActivitySortMode>('priority');
  const [search, setSearch] = useState('');
  const [showUnattachedOnly, setShowUnattachedOnly] = useState(false);
  const [subjects, setSubjects] = useState<ActivitySubjectCardType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<ActivitySubjectCardType | null>(
    null
  );
  const [selectedDetail, setSelectedDetail] = useState<ActivitySubjectDetail | null>(
    null
  );
  const [detailLoading, setDetailLoading] = useState(false);
  const [dismissingRecommendationId, setDismissingRecommendationId] = useState<
    string | null
  >(null);
  const [threadLivingCardEnabled, setThreadLivingCardEnabled] = useState(false);
  const [archivingSubjectKey, setArchivingSubjectKey] = useState<string | null>(null);
  const [preparingPlanSourceRunId, setPreparingPlanSourceRunId] = useState<string | null>(
    null
  );
  const [planPreparationError, setPlanPreparationError] = useState<string | null>(null);

  const loadSubjects = useCallback(async (options?: { background?: boolean }) => {
    if (!options?.background) setLoading(true);
    if (!options?.background) setError(null);

    try {
      const response = await listActivitySubjects();
      setSubjects(response.subjects);
      setThreadLivingCardEnabled(response.threadLivingCardEnabled === true);
    } catch (loadError) {
      const message =
        loadError instanceof Error
          ? loadError.message
          : 'Impossible de charger Activity.';

      if (options?.background) {
        console.warn('Activity background refresh failed', message);
      } else {
        setError(message);
      }
    } finally {
      if (!options?.background) setLoading(false);
    }
  }, []);
  const { apps, reload: reloadApps } = useAppWorkList();
  const rituals = useDueRituals();
  const handoffs = useWorkHandoffs();
  const isManager = useIsTenantManager();
  // Team and company scope (managers): the team's week, the review of the apps.
  const scope = isManager ? scopeOf(pageParams.get('portee')) : 'me';
  const refreshSubjects = useCallback(() => {
    void loadSubjects({ background: true });
    reloadApps();
  }, [loadSubjects, reloadApps]);
  const { handlers: gestures, dialogs: gestureDialogs } = useLivingWorkGestures({
    onChanged: () => {
      reloadApps();
      return loadSubjects({ background: true });
    },
    onTriaged: async (subject) => {
      if (selectedSubject && getActivitySubjectStableId(selectedSubject) === getActivitySubjectStableId(subject)) {
        await openSubject(subject);
      }
    },
    onError: setError,
  });
  const { onToolHandoff: handleToolHandoff, onFlowHandoff: handleFlowHandoff } = gestures;
  useThreadStateRealtime({
    enabled: threadLivingCardEnabled,
    onRefresh: refreshSubjects,
  });
  useProposalExpiryRefresh({
    threadStates: subjects.map((subject) => subject.threadState),
    onRefresh: refreshSubjects,
  });

  useEffect(() => {
    const refresh = () => refreshSubjects();
    window.addEventListener(ACCOUNT_TASK_COMPLETED_EVENT, refresh);
    return () => window.removeEventListener(ACCOUNT_TASK_COMPLETED_EVENT, refresh);
  }, [refreshSubjects]);

  const syncGmailAndLoadSubjects = async (background = false) => {
    try {
      await syncKnownGmailThreads();
    } catch (syncError) {
      console.warn('Gmail known-thread sync failed', syncError);
    }
    await loadSubjects({ background });
    if (background) reloadApps();
  };

  useEffect(() => {
    void syncGmailAndLoadSubjects();

    const refreshOnFocus = () => {
      void syncGmailAndLoadSubjects(true);
    };
    const refreshOnVisibility = () => {
      if (document.visibilityState === 'visible') {
        void syncGmailAndLoadSubjects(true);
      }
    };

    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnVisibility);

    return () => {
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnVisibility);
    };
  }, [loadSubjects]);

  const openSubject = async (subject: ActivitySubjectCardType) => {
    setSelectedSubject(subject);
    setDetailLoading(true);
    setSelectedDetail(null);

    try {
      const response = await getActivitySubjectDetail({
        subjectId: subject.subjectId,
        subjectKey: subject.subjectKey,
        subjectKind: subject.subjectKind,
        subjectLabel: subject.subjectLabel,
      });
      setSelectedDetail(response.detail);
    } catch (loadError) {
      setError(
        loadError instanceof Error
          ? loadError.message
          : 'Impossible de charger le compte.'
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDismissRecommendation = async (
    recommendation: ActivitySubjectRecommendation
  ) => {
    if (!selectedSubject) return;

    setDismissingRecommendationId(recommendation.id);
    try {
      await dismissActivityRecommendation({
        subjectId: selectedSubject.subjectId,
        subjectKey: selectedSubject.subjectKey,
        subjectKind: selectedSubject.subjectKind,
        subjectLabel: selectedSubject.subjectLabel,
        recommendationId: recommendation.id,
        recommendationLabel: recommendation.label,
        sourceRunId: recommendation.sourceRunId,
        sourceActionId: recommendation.sourceActionId,
        sourceActionIndex: recommendation.sourceActionIndex,
      });

      await Promise.all([
        loadSubjects({ background: true }),
        openSubject(selectedSubject),
      ]);
    } catch (dismissError) {
      setError(
        dismissError instanceof Error
          ? dismissError.message
          : 'Impossible de mettre à jour la recommandation.'
      );
    } finally {
      setDismissingRecommendationId(null);
    }
  };

  const handleArchiveSubject = async (subject: ActivitySubjectCardType) => {
    const subjectId = getActivitySubjectStableId(subject);
    setArchivingSubjectKey(subjectId);
    setError(null);

    try {
      await archiveActivitySubject({
        subjectId: subject.subjectId,
        subjectKey: subject.subjectKey,
        subjectKind: subject.subjectKind,
        subjectLabel: subject.subjectLabel,
      });
      setSubjects((currentSubjects) =>
        currentSubjects.filter((entry) => getActivitySubjectStableId(entry) !== subjectId)
      );
      if (selectedSubject && getActivitySubjectStableId(selectedSubject) === subjectId) {
        setSelectedSubject(null);
        setSelectedDetail(null);
      }
    } catch (archiveError) {
      setError(
        archiveError instanceof Error
          ? archiveError.message
          : 'Impossible d’archiver ce compte.'
      );
    } finally {
      setArchivingSubjectKey(null);
    }
  };

  const handlePreparePlan = async (sourceRunId: string) => {
    if (preparingPlanSourceRunId) return;

    setPreparingPlanSourceRunId(sourceRunId);
    setPlanPreparationError(null);
    try {
      const result = await prepareAccountTaskPlan({
        operation: 'prepare_plan',
        sourceRunId,
      });
      if (result.status !== 'ready') {
        throw new Error(result.message);
      }

      const selectedSubjectSnapshot = selectedSubject;
      await Promise.all([
        loadSubjects({ background: true }),
        selectedSubjectSnapshot
          ? openSubject(selectedSubjectSnapshot)
          : Promise.resolve(),
      ]);
    } catch (prepareError) {
      setPlanPreparationError(
        prepareError instanceof Error
          ? prepareError.message
          : 'Impossible de préparer les actions.'
      );
    } finally {
      setPreparingPlanSourceRunId(null);
    }
  };

  const baseSubjects = useMemo(
    () =>
      subjects
        .filter((subject) =>
          showUnattachedOnly
            ? isUnattachedActivitySubject(subject)
            : !isUnattachedActivitySubject(subject)
        )
        .sort((left, right) => sortActivitySubjects(left, right, sortMode)),
    [showUnattachedOnly, sortMode, subjects]
  );
  const filteredSubjects = useMemo(
    () => baseSubjects.filter((subject) => matchesSearch(subject, search)),
    [baseSubjects, search]
  );
  const unattachedCount = useMemo(
    () =>
      subjects.filter(isUnattachedActivitySubject).length,
    [subjects]
  );
  const livingAccounts = useMemo(
    () => projectActivityLivingAccounts(baseSubjects),
    [baseSubjects]
  );
  const livingSubjects = useMemo(
    () => livingAccounts.map((account) => account.subject),
    [livingAccounts]
  );
  const hasActiveFilters = search.trim() !== '' || showUnattachedOnly;
  const assistantContext = useMemo(() => {
    if (selectedDetail) {
      const selectedActions: DashboardChatPageAction[] = [
        buildOpenThreadPageAction(selectedDetail, 'activity.current_thread.open'),
      ];

      if (selectedDetail.nextStepAction) {
        selectedActions.push({
          id: 'activity.current_thread.next_step',
          kind: 'view_action',
          label: selectedDetail.nextStepLabel ?? selectedDetail.nextStepAction.label,
          description: selectedDetail.nextStepDescription ?? null,
          action: selectedDetail.nextStepAction,
          actionLabel: selectedDetail.nextStepAction.label,
        });
      }

      selectedDetail.recommendations.slice(0, 6).forEach((recommendation, index) => {
        if (!recommendation.action) return;
        selectedActions.push({
          id: `activity.current_thread.recommendation.${index}`,
          kind: 'view_action',
          label: recommendation.label,
          description: recommendation.summary,
          action: recommendation.action,
          actionLabel: recommendation.action.label,
        });
      });

      return {
        surface: 'home' as const,
        routePath: '/tasks',
        routeLabel: selectedDetail.subjectLabel,
        visibleSummary: [
          selectedDetail.headline,
          selectedDetail.nextStepLabel
            ? `Prochaine tâche : ${selectedDetail.nextStepLabel}`
            : null,
          selectedDetail.nextStepDescription,
          selectedDetail.thread[0]?.summary
            ? `Fil visible : ${selectedDetail.thread[0].summary}`
            : null,
        ]
          .filter(Boolean)
          .join(' '),
        visibleEntities: [
          selectedDetail.subjectLabel,
          selectedDetail.subjectSubtitle,
        ].filter((value): value is string => Boolean(value)),
        visibleItems: [
          buildThreadPageItem(
            selectedDetail,
            'activity.current_thread',
            'activity.current_thread.open'
          ),
        ],
        availableActions: selectedActions.slice(0, 12),
      };
    }

    const contextSubjects = threadLivingCardEnabled ? livingSubjects : filteredSubjects;
    const topSubjects = contextSubjects
      .slice(0, 5)
      .map(
        (subject) =>
          `${subject.subjectLabel}${
            subject.nextStepLabel ? ` — ${subject.nextStepLabel}` : ''
          }`
      );

    return {
      surface: 'home' as const,
      routePath: '/tasks',
      routeLabel: 'Mon travail',
      visibleSummary: loading
        ? 'Chargement des comptes.'
        : `${contextSubjects.length} compte${contextSubjects.length > 1 ? 's' : ''}. ${
            topSubjects.length > 0 ? topSubjects.join('; ') : 'Aucun compte visible.'
          }${activeObjectivesSummary ? ` Objectifs actifs : ${activeObjectivesSummary}` : ''}`,
      visibleEntities: contextSubjects.slice(0, 8).map((subject) => subject.subjectLabel),
      visibleItems: contextSubjects
        .slice(0, 8)
        .map((subject, index) =>
          buildThreadPageItem(
            subject,
            `activity.visible_thread.${index}`,
            `activity.visible_thread.${index}`
          )
        ),
      availableActions: contextSubjects
        .slice(0, 5)
        .map((subject, index) =>
          buildOpenThreadPageAction(subject, `activity.visible_thread.${index}`)
        ),
    };
  }, [
    filteredSubjects,
    livingSubjects,
    loading,
    selectedDetail,
    threadLivingCardEnabled,
    activeObjectivesSummary,
  ]);
  useRegisterAppAssistantContext(assistantContext);

  const resetFilters = () => {
    setSearch('');
    setShowUnattachedOnly(false);
  };

  const searchField = (
    <div className="relative max-w-xl">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-domino-inkSoft" />
      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        aria-label={threadLivingCardEnabled ? 'Rechercher un travail, un compte ou une application' : 'Rechercher un compte'}
        placeholder='Rechercher'
        className="h-9 w-full rounded-lg border border-domino-cardBorder bg-domino-white pl-10 pr-4 text-sm text-domino-ink outline-none transition placeholder:text-domino-inkSoft/70 focus:border-domino-prune focus:ring-2 focus:ring-domino-prune/15"
      />
    </div>
  );

  return (
    <div className="min-h-full bg-domino-ivory px-4 py-4 sm:px-6">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight text-domino-ink">
              {mode === 'subjects' ? 'Mon travail' : 'Historique'}
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {mode === 'subjects' && isManager && <ScopeSwitch scope={scope} />}
            {mode === 'subjects' && scope === 'me' && unattachedCount > 0 && (
              <button
                type="button"
                onClick={() => setShowUnattachedOnly((current) => !current)}
                className={`text-sm font-medium transition-colors ${
                  showUnattachedOnly
                    ? 'text-domino-aubergine'
                    : 'text-domino-inkSoft hover:text-domino-ink'
                }`}
              >
                À rattacher ({unattachedCount})
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                const next = mode === 'subjects' ? 'history' : 'subjects';
                setMode(next);
                const params = new URLSearchParams(pageParams);
                if (next === 'history') params.set('history', '1');
                else { params.delete('history'); params.delete('q'); }
                setPageParams(params);
              }}
              className="py-2 text-sm font-medium text-domino-inkSoft transition-colors hover:text-domino-ink"
            >
              {mode === 'subjects' ? 'Historique' : 'Mon travail'}
            </button>
          </div>
        </div>

        <div>
        <div className="min-w-0">
        {mode === 'subjects' ? (
          scope === 'team' ? <TeamWeek /> : scope === 'company' ? <PlaysReview /> : <>
            {!threadLivingCardEnabled && <><TodayObjectives /><TodayRituals /></>}
            <AppInbox />
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
              </div>
            ) : error ? (
              <div className="border-t border-red-200 py-4 text-sm text-red-700">
                {error}
              </div>
            ) : subjects.length === 0 && (apps.length === 0 || !threadLivingCardEnabled) && !hasActiveFilters
              && handoffs.incoming.length === 0 && !rituals.rituals?.length ? (
              <ActivityEmptyState />
            ) : (
              <div className="space-y-3">
                {planPreparationError && (
                  <div
                    role="alert"
                    className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
                  >
                    {planPreparationError}
                  </div>
                )}
                {threadLivingCardEnabled ? (
                  <TodayWorkList
                    subjects={baseSubjects}
                    apps={showUnattachedOnly ? [] : apps}
                    search={search}
                    toolbar={searchField}
                    rituals={rituals.rituals ?? []}
                    today={rituals.today}
                    onRitualChange={rituals.replaceOccurrence}
                    handoffs={handoffs}
                    objectives={<WorkObjectivesTab />}
                    onAppWorkChange={refreshSubjects}
                    {...gestures}
                  />
                ) : (
                  <>
                    {searchField}
                    <ActivityActionQueue
                      subjects={filteredSubjects}
                      onTriageAction={(subject, action, reason) => gestures.onTriageAction(subject, action, reason)}
                      onToolHandoff={handleToolHandoff}
                      onFlowHandoff={handleFlowHandoff}
                      onPreparePlan={(sourceRunId) => {
                        void handlePreparePlan(sourceRunId);
                      }}
                      preparingPlanSourceRunId={preparingPlanSourceRunId}
                      triagingActionId={gestures.triagingActionId}
                      launchStatuses={gestures.launchStatuses}
                    />
                  </>
                )}

                {!threadLivingCardEnabled && (
                  <details
                    id="liste-comptes"
                    className="group border-t border-domino-cardBorder"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between py-4 text-sm font-medium text-domino-inkSoft transition hover:text-domino-ink">
                      <span>Tous les comptes</span>
                      <span className="tabular-nums">
                        {filteredSubjects.length}
                        {hasActiveFilters ? ` / ${subjects.length}` : ''}
                      </span>
                    </summary>
                    <div className="pb-6 pt-2">
                      <ActivitySubjectTable
                        subjects={filteredSubjects}
                        archivingSubjectKey={archivingSubjectKey}
                        sortMode={sortMode}
                        hasActiveFilters={hasActiveFilters}
                        onSortModeChange={setSortMode}
                        onResetFilters={resetFilters}
                        onOpen={(subject) => {
                          void openSubject(subject);
                        }}
                        onArchive={(subject) => {
                          void handleArchiveSubject(subject);
                        }}
                      />
                    </div>
                  </details>
                )}
              </div>
            )}
          </>
        ) : (
          <ActivityJournalView />
        )}
        </div>
        </div>
      </div>

      <ActivitySubjectDrawer
        open={selectedSubject !== null}
        detail={selectedDetail}
        loading={detailLoading}
        dismissingRecommendationId={dismissingRecommendationId}
        onClose={() => {
          setSelectedSubject(null);
          setSelectedDetail(null);
        }}
        onDismissRecommendation={(recommendation) => {
          void handleDismissRecommendation(recommendation);
        }}
        onToolHandoff={(recommendation, toolAction) => {
          if (!selectedSubject) return;
          handleToolHandoff(selectedSubject, toActionSummary(recommendation), toolAction);
        }}
        onFlowHandoff={(recommendation, flowAction) => {
          if (!selectedSubject) return;
          handleFlowHandoff(selectedSubject, toActionSummary(recommendation), flowAction);
        }}
        onPreparePlan={(sourceRunId) => {
          void handlePreparePlan(sourceRunId);
        }}
        preparingPlanSourceRunId={preparingPlanSourceRunId}
      />
      {gestureDialogs}
    </div>
  );
};
